import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import { LogOut, Package, Mail, Phone, MapPin, Calendar, User as UserIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/quotes/mine`, { withCredentials: true });
        setQuotes(res.data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <Header />
      <section className="bg-gray-50 min-h-[calc(100vh-200px)] py-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Profile card */}
          <div className="bg-white rounded-md shadow-sm border border-gray-100 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full object-cover border-2 border-[color:var(--brand-red)]" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[color:var(--brand-teal)] text-white flex items-center justify-center">
                <UserIcon size={36} />
              </div>
            )}
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-[color:var(--brand-red)] font-semibold">Welcome back</div>
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">{user?.name}</h1>
              <div className="text-gray-600 mt-1 flex items-center gap-2 text-sm">
                <Mail size={14} /> {user?.email}
              </div>
            </div>
            <button
              onClick={logout}
              className="btn-red font-display uppercase tracking-wider px-6 py-3 rounded text-sm inline-flex items-center gap-2"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>

          {/* Quotes */}
          <div className="mt-10">
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <Package className="text-[color:var(--brand-red)]" size={22} /> My Quote Inquiries
            </h2>
            <div className="h-1 w-16 bg-[color:var(--brand-red)] mt-2 mb-6" />

            {loading ? (
              <div className="text-gray-500">Loading...</div>
            ) : quotes.length === 0 ? (
              <div className="bg-white rounded-md p-10 text-center border border-dashed border-gray-200">
                <Package className="mx-auto text-gray-300" size={44} />
                <p className="mt-3 text-gray-600">You haven't submitted any quote inquiries yet.</p>
                <a href="/#contact" className="btn-red inline-block font-display uppercase tracking-wider px-6 py-3 rounded text-sm mt-4">
                  Request a Quote
                </a>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {quotes.map((q) => (
                  <div key={q.inquiry_id} className="bg-white rounded-md border border-gray-100 p-5 shadow-sm hover:border-[color:var(--brand-red)]/40 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-gray-800">{q.full_name}</div>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Calendar size={12} /> {new Date(q.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1.5 text-sm text-gray-600">
                      <div className="flex items-center gap-2"><Phone size={14} className="text-[color:var(--brand-teal)]" /> {q.phone}</div>
                      <div className="flex items-center gap-2"><Mail size={14} className="text-[color:var(--brand-teal)]" /> {q.email}</div>
                      {(q.moving_from || q.moving_to) && (
                        <div className="flex items-start gap-2">
                          <MapPin size={14} className="text-[color:var(--brand-teal)] mt-0.5" />
                          <span>{q.moving_from || "—"} → {q.moving_to || "—"}</span>
                        </div>
                      )}
                    </div>
                    {q.message && (
                      <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-700 italic">"{q.message}"</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Dashboard;
